import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Button,
  CardContent,
  TextField,
  InputAdornment,
  TablePagination,
  Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ConfirmationNumber as CouponIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Redeem as RedeemIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { multiTenantCouponAPI } from '../../services/api';
import api from '../../services/api';
import ModernButton from '../../components/Common/ModernButton';
import ModernCard from '../../components/Common/ModernCard';
import ModernTextField from '../../components/Common/ModernTextField';
import IconContainer from '../../components/Common/IconContainer';
import ResponsivePageHeader from '../../components/Common/ResponsivePageHeader';
import useResponsive from '../../hooks/useResponsive';
import { logger } from '../../utils/logger';

// Types based on backend_new API
interface Offer {
  id: string; // PassKit offer ID
  campaign_id: string;
  name: string;
  campaign_name?: string;
  issue_start_date?: string;
  issue_end_date?: string;
  status?: string;
}

interface Coupon {
  id: string; // PassKit coupon ID
  offer_id: string;
  campaign_id: string;
  campaign_name?: string;
  company_name?: string;
  company_id?: string;
  external_id?: string;
  person_forename?: string;
  person_surname?: string;
  person_email?: string;
  person_mobile?: string;
  person_display_name?: string;
  status: string; // UNREDEEMED, REDEEMED, VOIDED
  created?: string;
  updated?: string;
  redeemed_at?: string;
}

const CouponsNew: React.FC = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const isAdmin = user?.role === 'admin';

  const [offers, setOffers] = useState<Offer[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Statistics
  const totalCoupons = coupons.length;
  const redeemedCoupons = coupons.filter(c => c.status === 'REDEEMED').length;
  const unredeemedCoupons = coupons.filter(c => c.status === 'UNREDEEMED').length;
  const voidedCoupons = coupons.filter(c => c.status === 'VOIDED').length;



  // Check if offer is expired
  const isOfferExpired = (offer: Offer): boolean => {
    if (!offer.issue_end_date) return false;
    try {
      const endDate = new Date(offer.issue_end_date);
      const now = new Date();
      return now > endDate;
    } catch (e) {
      return false;
    }
  };

  // Load ALL coupons
  const loadAllCoupons = useCallback(async () => {
    setCouponsLoading(true);
    try {
      const data = await multiTenantCouponAPI.getCoupons({});
      setCoupons(data);
    } catch (error: any) {
      showNotification({ message: 'Greška prilikom učitavanja kupona: ' + error.message, type: 'error' });
      setCoupons([]);
    } finally {
      setCouponsLoading(false);
    }
  }, [showNotification]);

  // Load ALL offers
  const loadAllOffers = useCallback(async () => {
    try {
      const data = await multiTenantCouponAPI.getOffers();
      // Filter out expired offers
      const activeOffers = data.filter((offer: Offer) => !isOfferExpired(offer));
      setOffers(activeOffers);
    } catch (error: any) {
      showNotification({ message: 'Greška prilikom učitavanja ponuda: ' + error.message, type: 'error' });
    }
  }, [showNotification]);

  useEffect(() => {
    loadAllOffers(); // Load all offers on mount
    loadAllCoupons(); // Load all coupons on mount
  }, [loadAllOffers, loadAllCoupons]);

  const handleRedeemCoupon = async (couponId: string) => {
    if (!globalThis.confirm('Da li ste sigurni da želite da iskorisite ovaj kupon?')) return;
    try {
      await multiTenantCouponAPI.redeemCoupon(couponId, {});
      showNotification({ message: 'Kupon uspešno iskorišćen', type: 'success' });
      loadAllCoupons();
    } catch (error: any) {
      let errorMessage = error.response?.data?.detail || error.message || 'Greška prilikom iskorišćavanja';

      // Format date if it's a time restriction error
      if (errorMessage.includes('can be redeemed from')) {
        const match = errorMessage.match(/from (.+)$/);
        if (match) {
          const dateStr = match[1];
          try {
            const date = new Date(dateStr);
            const formattedDate = date.toLocaleString('sr-RS', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit'
            });
            errorMessage = `Kupon može biti iskorišćen od ${formattedDate}`;
          } catch {
            // Keep original message if parsing fails
          }
        }
      }

      showNotification({ message: errorMessage, type: 'error' });
    }
  };

  const handleVoidCoupon = async (couponId: string) => {
    if (!globalThis.confirm('Da li ste sigurni da želite da poništite ovaj kupon?')) return;
    try {
      await multiTenantCouponAPI.voidCoupon(couponId);
      showNotification({ message: 'Kupon uspešno poništen', type: 'success' });
      loadAllCoupons();
    } catch (error: any) {
      showNotification({ message: 'Greška prilikom poništavanja: ' + error.message, type: 'error' });
    }
  };

  const handleCopyPassLink = (coupon: Coupon) => {
    const passUrl = `https://pub1.pskt.io/${coupon.id}`;
    navigator.clipboard.writeText(passUrl).then(() => {
      showNotification({ message: 'Pass link kopiran u clipboard!', type: 'success' });
    }).catch(() => {
      showNotification({ message: 'Greška prilikom kopiranja linka', type: 'error' });
    });
  };

  const handleViewDetails = (coupon: Coupon) => {
    setSelectedCoupon(coupon);
    setOpenDetailsDialog(true);
  };

  const filteredCoupons = coupons.filter(
    (coupon) =>
      coupon.person_forename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coupon.person_surname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coupon.person_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coupon.person_display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coupon.external_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coupon.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coupon.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedCoupons = filteredCoupons.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'UNREDEEMED':
        return 'info';
      case 'REDEEMED':
        return 'success';
      case 'VOIDED':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'UNREDEEMED':
        return 'Neiskorišćen';
      case 'REDEEMED':
        return 'Iskorišćen';
      case 'VOIDED':
        return 'Poništen';
      default:
        return status;
    }
  };

  return (
    <Box>
      <ResponsivePageHeader
        title="Upravljanje Kuponima"
        subtitle="Kreirajte i upravljajte kuponima za izabranu ponudu"
        actions={
          <ModernButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateDialog(true)}
          >
            Novi Kupon
          </ModernButton>
        }
      />

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: { xs: 2, md: 3 }, mb: { xs: 3, md: 4 } }}>
          <ModernCard>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }}>
                <IconContainer color="primary" variant="soft">
                  <CouponIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                    {totalCoupons}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Ukupno Kupona
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>

          <ModernCard>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }}>
                <IconContainer color="info" variant="soft">
                  <CheckCircleIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                    {unredeemedCoupons}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Neiskorišćeni
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>

          <ModernCard>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }}>
                <IconContainer color="success" variant="soft">
                  <RedeemIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                    {redeemedCoupons}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Iskorišćeno
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>

          <ModernCard>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }}>
                <IconContainer color="error" variant="soft">
                  <CancelIcon />
                </IconContainer>
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                    {voidedCoupons}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                    Poništeni
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </ModernCard>
        </Box>

      {/* Search and Table */}
      <ModernCard>
          <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <TextField
              fullWidth
              placeholder="Pretraži kupone po imenu, email-u ili External ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: { xs: 2, md: 3 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchTerm('')} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {couponsLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Desktop: Table View */}
                {!isMobile && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Ime</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Telefon</TableCell>
                          {isAdmin && <TableCell>Kompanija</TableCell>}
                          <TableCell>External ID</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Kreirano</TableCell>
                          <TableCell>Akcije</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedCoupons.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                              <Typography color="text.secondary">
                                {coupons.length === 0 ? 'Nema kreiranih kupona za ovu ponudu' : 'Nema pronađenih kupona'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedCoupons.map((coupon) => (
                            <TableRow key={coupon.id}>
                              <TableCell>
                                {coupon.person_display_name || `${coupon.person_forename || ''} ${coupon.person_surname || ''}`.trim() || '-'}
                              </TableCell>
                              <TableCell>{coupon.person_email || '-'}</TableCell>
                              <TableCell>{coupon.person_mobile || '-'}</TableCell>
                              {isAdmin && (
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {coupon.company_name || '-'}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace">
                                  {coupon.external_id || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getStatusLabel(coupon.status)}
                                  size="small"
                                  color={getStatusColor(coupon.status) as any}
                                />
                              </TableCell>
                              <TableCell>
                                {coupon.created ? new Date(coupon.created).toLocaleDateString() : '-'}
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyPassLink(coupon)}
                                  title="Kopiraj Pass Link"
                                  color="primary"
                                >
                                  <ContentCopyIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewDetails(coupon)}
                                  title="Detalji"
                                >
                                  <VisibilityIcon />
                                </IconButton>
                                {coupon.status === 'UNREDEEMED' && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRedeemCoupon(coupon.id)}
                                    title="Iskoristi"
                                    color="success"
                                  >
                                    <RedeemIcon />
                                  </IconButton>
                                )}
                                {coupon.status !== 'VOIDED' && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleVoidCoupon(coupon.id)}
                                    title="Poništi"
                                    color="error"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Mobile: Card View */}
                {isMobile && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {paginatedCoupons.length === 0 ? (
                      <Box textAlign="center" py={4}>
                        <Typography color="text.secondary">
                          {coupons.length === 0 ? 'Nema kreiranih kupona za ovu ponudu' : 'Nema pronađenih kupona'}
                        </Typography>
                      </Box>
                    ) : (
                      paginatedCoupons.map((coupon) => (
                        <ModernCard key={coupon.id}>
                          <CardContent sx={{ p: 2 }}>
                            {/* Header with name and status */}
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                              <Box flex={1}>
                                <Typography variant="subtitle1" fontWeight={600} mb={0.5}>
                                  {coupon.person_display_name || `${coupon.person_forename || ''} ${coupon.person_surname || ''}`.trim() || '-'}
                                </Typography>
                                {isAdmin && coupon.company_name && (
                                  <Typography variant="caption" color="text.secondary">
                                    {coupon.company_name}
                                  </Typography>
                                )}
                              </Box>
                              <Chip
                                label={getStatusLabel(coupon.status)}
                                size="small"
                                color={getStatusColor(coupon.status) as any}
                              />
                            </Box>

                            {/* Contact info */}
                            <Box display="flex" flexDirection="column" gap={0.5} mb={1.5}>
                              {coupon.person_email && (
                                <Box display="flex" alignItems="center" gap={1}>
                                  <EmailIcon fontSize="small" color="action" />
                                  <Typography variant="body2" color="text.secondary">
                                    {coupon.person_email}
                                  </Typography>
                                </Box>
                              )}
                              {coupon.person_mobile && (
                                <Box display="flex" alignItems="center" gap={1}>
                                  <PhoneIcon fontSize="small" color="action" />
                                  <Typography variant="body2" color="text.secondary">
                                    {coupon.person_mobile}
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* External ID and Date */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  External ID
                                </Typography>
                                <Typography variant="body2" fontFamily="monospace">
                                  {coupon.external_id || '-'}
                                </Typography>
                              </Box>
                              <Box textAlign="right">
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Kreirano
                                </Typography>
                                <Typography variant="body2">
                                  {coupon.created ? new Date(coupon.created).toLocaleDateString() : '-'}
                                </Typography>
                              </Box>
                            </Box>

                            <Divider sx={{ mb: 1.5 }} />

                            {/* Action Buttons */}
                            <Box display="flex" gap={1} flexWrap="wrap">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ContentCopyIcon />}
                                onClick={() => handleCopyPassLink(coupon)}
                                sx={{ flex: 1, minWidth: 120 }}
                              >
                                Kopiraj Link
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleViewDetails(coupon)}
                                sx={{ flex: 1, minWidth: 120 }}
                              >
                                Detalji
                              </Button>
                              {coupon.status === 'UNREDEEMED' && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  startIcon={<RedeemIcon />}
                                  onClick={() => handleRedeemCoupon(coupon.id)}
                                  sx={{ flex: 1, minWidth: 120 }}
                                >
                                  Iskoristi
                                </Button>
                              )}
                              {coupon.status !== 'VOIDED' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => handleVoidCoupon(coupon.id)}
                                  sx={{ flex: 1, minWidth: 120 }}
                                >
                                  Poništi
                                </Button>
                              )}
                            </Box>
                          </CardContent>
                        </ModernCard>
                      ))
                    )}
                  </Box>
                )}

                <TablePagination
                  component="div"
                  count={filteredCoupons.length}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(Number.parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  labelRowsPerPage="Redova po stranici:"
                  labelDisplayedRows={({ from, to, count }) => {
                    const total = count !== -1 ? count : 'više od ' + to;
                    return `${from}-${to} od ${total}`;
                  }}
                />
              </>
            )}
          </CardContent>
        </ModernCard>

      {/* Create Coupon Dialog */}
      <CreateCouponDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        offers={offers}
        onSuccess={() => {
          setOpenCreateDialog(false);
          loadAllCoupons();
        }}
      />

      {/* Coupon Details Dialog */}
      <CouponDetailsDialog
        open={openDetailsDialog}
        onClose={() => {
          setOpenDetailsDialog(false);
          setSelectedCoupon(null);
        }}
        coupon={selectedCoupon}
      />
    </Box>
  );
};

// Create Coupon Dialog Component
const CreateCouponDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  offers: Offer[];
  onSuccess: () => void;
}> = ({ open, onClose, offers, onSuccess }) => {
  const { showNotification } = useNotification();
  const [selectedOfferId, setSelectedOfferId] = useState('');
  const [formData, setFormData] = useState<any>({
    external_id: '',
    person: {
      forename: '',
      surname: '',
      emailAddress: '',
      mobileNumber: '',
      displayName: '',
    }
  });
  const [saving, setSaving] = useState(false);
  const [templateFields, setTemplateFields] = useState<any[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);

  // Load template fields when offer is selected
  useEffect(() => {
    const loadTemplateFields = async () => {
      if (!selectedOfferId) {
        setTemplateFields([]);
        return;
      }

      const selectedOffer = offers.find(o => o.id === selectedOfferId);
      if (!selectedOffer) return;

      setLoadingFields(true);
      try {
        const response = await api.get(
          `/coupons/offers/${selectedOfferId}/template-fields?campaign_id=${selectedOffer.campaign_id}`
        );

        if (response.data) {
          setTemplateFields(response.data.fields || []);
        } else {
          setTemplateFields([]);
        }
      } catch (error) {
        logger.error('Error loading template fields:', error);
        setTemplateFields([]);
      } finally {
        setLoadingFields(false);
      }
    };

    loadTemplateFields();
  }, [selectedOfferId, offers]);

  // Helper function to get nested value from formData
  const getFieldValue = (fieldName: string): any => {
    if (fieldName.includes('.')) {
      const parts = fieldName.split('.');
      let value: any = formData;
      for (const part of parts) {
        value = value?.[part];
      }
      return value || '';
    }
    return (formData as any)[fieldName] || '';
  };

  // Helper function to set nested value in formData
  const setFieldValue = (fieldName: string, value: any) => {
    if (fieldName.includes('.')) {
      const parts = fieldName.split('.');
      const newFormData = { ...formData };
      let current: any = newFormData;

      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }

      current[parts[parts.length - 1]] = value;
      setFormData(newFormData);
    } else {
      setFormData({ ...formData, [fieldName]: value });
    }
  };

  // Render field based on type
  const renderField = (field: any) => {
    const fieldProps = {
      key: field.name,
      label: field.label,
      value: getFieldValue(field.name),
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setFieldValue(field.name, e.target.value),
      required: field.required,
      type: field.type || 'text',
      helperText: field.description
    };

    return <ModernTextField {...fieldProps} />;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOfferId) {
      showNotification({ message: 'Molimo izaberite ponudu', type: 'error' });
      return;
    }

    const selectedOffer = offers.find(o => o.id === selectedOfferId);
    if (!selectedOffer) return;

    setSaving(true);
    try {
      // Transform nested person.* fields to person_* format for API
      const apiData: any = {
        campaign_id: selectedOffer.campaign_id,
        offer_id: selectedOffer.id,
      };

      // Map person.* fields to person_* fields
      const personData = formData as any;
      if (personData.person) {
        if (personData.person.forename) apiData.person_forename = personData.person.forename;
        if (personData.person.surname) apiData.person_surname = personData.person.surname;
        if (personData.person.emailAddress) apiData.person_email = personData.person.emailAddress;
        if (personData.person.mobileNumber) apiData.person_mobile = personData.person.mobileNumber;
        if (personData.person.displayName) apiData.person_display_name = personData.person.displayName;
      }

      // Add external_id if provided
      if (personData.external_id) {
        apiData.external_id = personData.external_id;
      }

      logger.log('Sending coupon data:', apiData);

      await multiTenantCouponAPI.createCoupon(apiData);
      showNotification({ message: 'Kupon uspešno kreiran', type: 'success' });

      // Reset form
      setFormData({
        external_id: '',
        person: {
          forename: '',
          surname: '',
          emailAddress: '',
          mobileNumber: '',
          displayName: '',
        }
      });
      setSelectedOfferId('');
      onSuccess();
    } catch (error: any) {
      showNotification({ message: 'Greška: ' + error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Kreiraj Novi Kupon</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Offer Selection */}
            <FormControl fullWidth required>
              <InputLabel>Izaberite Ponudu</InputLabel>
              <Select
                value={selectedOfferId}
                label="Izaberite Ponudu"
                onChange={(e) => setSelectedOfferId(e.target.value)}
              >
                {offers.map((offer) => (
                  <MenuItem key={offer.id} value={offer.id}>
                    {offer.name} {offer.campaign_name ? `(${offer.campaign_name})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Dynamic fields from template */}
            {loadingFields ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              templateFields.map((field) => renderField(field))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Otkaži</Button>
          <ModernButton
            type="submit"
            variant="contained"
            disabled={saving || !selectedOfferId || loadingFields}
          >
            {saving ? 'Kreiranje...' : 'Kreiraj Kupon'}
          </ModernButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// Coupon Details Dialog Component
const CouponDetailsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  coupon: Coupon | null;
}> = ({ open, onClose, coupon }) => {
  if (!coupon) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalji Kupona</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">ID Kupona</Typography>
            <Typography variant="body1" fontFamily="monospace">{coupon.id}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">External ID</Typography>
            <Typography variant="body1" fontFamily="monospace">{coupon.external_id || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Ime</Typography>
            <Typography variant="body1">{coupon.person_forename || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Prezime</Typography>
            <Typography variant="body1">{coupon.person_surname || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Email</Typography>
            <Typography variant="body1">{coupon.person_email || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Telefon</Typography>
            <Typography variant="body1">{coupon.person_mobile || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Box mt={0.5}>
              <Chip
                label={coupon.status}
                size="small"
                color={(() => {
                  if (coupon.status === 'UNREDEEMED') return 'info';
                  if (coupon.status === 'REDEEMED') return 'success';
                  return 'default';
                })()}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Kampanja</Typography>
            <Typography variant="body1">{coupon.campaign_name || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Kreirano</Typography>
            <Typography variant="body1">{coupon.created ? new Date(coupon.created).toLocaleString() : '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Ažurirano</Typography>
            <Typography variant="body1">{coupon.updated ? new Date(coupon.updated).toLocaleString() : '-'}</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zatvori</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CouponsNew;
